"use client";
import Fest24TextLogo from "@/assets/logo/Fest24TextLogo";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
    Calendar,
    MapPin,
    Play,
    Pause,
    X,
    Square,
    SkipForward,
    Shuffle,
    ListMusic,
    PauseIcon,
    PlayIcon,
    Headphones,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Separator } from "@/components/ui/separator";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

import { isInternalLink } from "@/lib/fest-utils";
import { Link } from "gatsby";

import PLAYLIST from "../../../data/Playlist.json";
import Sound from "react-sound";

const iconColor = "white";
const iconFadedColor = "grey";

import {
    radioPlay,
    radioPause,
    radioStop,
    radioNextTrack,
    radioShuffleOff,
    radioShuffleOn,
    playTrackByTrackId,
} from "@/actions/index";
import { connect } from "react-redux";
import { useAudioPlayer } from "react-use-audio-player";
import { prettyPrintHtmlEntitiesInString } from "@/lib/fest-utils";

const RadioPlayer = (props) => {
    const { dispatch, radio } = props;
    const { currentTrack, playStatus, shuffleMode } = radio;

    const { load } = useAudioPlayer();

    const updateMetadata = (currentTrack) => {
        const windowNavigator = typeof navigator === "undefined" ? {} : navigator;
        if ("mediaSession" in windowNavigator) {
            windowNavigator.mediaSession.metadata = new MediaMetadata({
                title: currentTrack.track,
                artist: currentTrack.artist,
                album: "FEST Radio",
                artwork: [
                    {
                        src: withPrefix("/favicons/android-chrome-36x36.png"),
                        sizes: "36x36",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-48x48.png"),
                        sizes: "48x48",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-72x72.png"),
                        sizes: "72x72",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-96x96.png"),
                        sizes: "96x96",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-144x144.png"),
                        sizes: "144x144",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-192x192.png"),
                        sizes: "192x192",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-256x256.png"),
                        sizes: "256x256",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-384x384.png"),
                        sizes: "384x384",
                        type: "image/png",
                    },
                    {
                        src: withPrefix("/favicons/android-chrome-512x512.png"),
                        sizes: "512x512",
                        type: "image/png",
                    },
                ],
            });

            windowNavigator.mediaSession.setActionHandler("play", () => {
                dispatch(radioPlay());
            });
            windowNavigator.mediaSession.setActionHandler("pause", () => {
                dispatch(radioPause());
            });
            windowNavigator.mediaSession.setActionHandler("nexttrack", () => {
                dispatch(radioNextTrack());
            });
        }
    };



    const togglePlayPause = () => {
        if (radio.playStatus === Sound.status.PLAYING) {
            dispatch(radioPause());
        } else if (radio.currentTrack) {
            dispatch(radioPlay());
        }
    };

    const toggleShuffleMode = () => {
        if (radio.shuffleMode) {
            dispatch(radioShuffleOff());
        } else {
            dispatch(radioShuffleOn());
        }
    };

    const playNextTrack = () => {
        dispatch(radioNextTrack());
    };



    const [sticky, setSticky] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const handleScroll = useCallback(() => {
        setSticky(window.scrollY >= 50);
    }, []);

    const handleResize = useCallback(() => {
        if (window.innerWidth >= 768) setIsOpen(false);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        };
    }, [handleScroll, handleResize]);


    return (
        <>
            {currentTrack && currentTrack.url && (
                <Sound
                    url={currentTrack.url}
                    playStatus={playStatus}
                    onFinishedPlaying={() => {
                        dispatch(radioNextTrack());
                    }}
                />
            )}
            <div className="fixed bottom-0 right-0 left-0 z-10000 ">
                <div className="flex w-full px-2 py-4">
                    <div
                        className={cn(
                            "w-full flex flex-nowrap items-center h-fit justify-between items-center gap-2 rounded-lg",
                            sticky
                                ? "px-2 md:px-4 py-2.5 bg-pink-400/20 backdrop-blur-3xl border border-border/20 shadow-2xl shadow-primary/5l"
                                : "px-2 md:px-4 py-2.5 bg-pink-400/20 backdrop-blur-3xl border border-border/20 shadow-2xl shadow-primary/5",
                        )}
                    >
                        <div className="basis-auto order-1 flex text-left justify-center ">

                            <Button
                                aria-label="Play/Pause"
                                onClick={togglePlayPause}
                                className="hover:cursor-pointer "
                            >

                                {playStatus === Sound.status.PLAYING ? (
                                    <PauseIcon size={32} color="white" />
                                ) : (
                                    <PlayIcon size={32} color="white" />
                                )}
                            </Button>

                            {playStatus === Sound.status.PLAYING && (
                                <Button
                                    className="hover:cursor-pointer hidden lg:block"
                                    aria-label="Stop"
                                    onClick={() => dispatch(radioStop())}
                                >
                                    <Square size={32} color="white" />
                                </Button>
                            )}

                            <Button className="hover:cursor-pointer" aria-label="Next Track" onClick={playNextTrack}>
                                <SkipForward size={32} color="white" />
                            </Button>

                            <Button aria-label="Toggle Shuffle" onClick={toggleShuffleMode} 
                                    className="hover:cursor-pointer hidden lg:block">
                                {shuffleMode ?
                                    (
                                        <Shuffle size={32} color="white" />
                                    ) :
                                    (
                                        <Shuffle size={32} color={iconFadedColor} />
                                    )
                                }
                            </Button>
                        </div>

                        <div className="basis-fit order-2 align-left">
                            {(currentTrack || playStatus !== Sound.status.STOPPED) && (
                                <div>
                                    {currentTrack.artist ? (
                                        <div className="truncate font-(family-name:--font-headline) w-fit max-w-fit text-ellipsis text-white"
                                            title={prettyPrintHtmlEntitiesInString(
                                                `${currentTrack.artist} - ${currentTrack.track}`
                                            )}
                                        >
                                            <Link to={`/bands/${currentTrack.slug}`}>
                                                {prettyPrintHtmlEntitiesInString(currentTrack.artist)}
                                            </Link>{" "}
                                            - {currentTrack.track}
                                        </div>
                                    ) : (
                                        <span  className="truncate font-(family-name:--font-headline) w-fit max-w-fit text-ellipsis text-white">
                                            FEST 24 RADIO
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className=" basis-auto flex order-3 " >
                            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                                <SheetTrigger
                                    render={
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="rounded-full border border-border p-2 outline-none flex items-center justify-center cursor-pointer hover:bg-muted transition-colors h-10 w-10"
                                            aria-label="Open Playlist"
                                        >

                                            <ListMusic size={32} color="white" />
                                        </Button>
                                    }
                                >
                                    <span className="sr-only">Open Radio</span>
                                </SheetTrigger>
                                <SheetContent
                                    showCloseButton={false}
                                    side="right"
                                    className=" dark min-w-80 p-0 gap-0"
                                >
                                    <ScrollArea className="h-full px-4 py-4 overflow-y-auto ">
                                        <SheetHeader className="p-0">
                                            <SheetClose className="absolute top-5 right-4 rounded-lg dark:bg-white bg-black dark:text-black text-white p-2 cursor-pointer ">
                                                <X size={16} />
                                            </SheetClose>
                                            <span className="uppercase text-3xl font-extrabold">#FEST24 RADIO</span><br />
                                        </SheetHeader>
                                        <div className="flex flex-col gap-2 pt-2">
                                            <ol>
                                                {PLAYLIST.map((track) =>
                                                    <li className="py-2 hover:bg-gray-800 hover:cursor-pointer  font-(family-name:--font-headline)" key={track.trackId} onClick={() => dispatch(playTrackByTrackId(track.trackId))}>
                                                        <span className="text-gray-200">{track.track}</span><br />
                                                        <span className="font-semibold uppercase text-(--color-fest24-teal)">{track.artist}</span>
                                                    </li>
                                                )}
                                            </ol>
                                        </div>
                                        <div className="mt-4">
                                        </div>
                                    </ScrollArea>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    radio: state.radio,
});


export const ConnectedRadioPlayer = connect(mapStateToProps)(RadioPlayer);